import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { isAnnotationContextGroup } from '../../contexts/AnnotationContext';
import { RendererContext } from '../../contexts/RendererContext';
import { useAnnotationTree } from './useAnnotationContext';
import { useRerender } from '../useRerender';
import { AnnotationsWithIndices, AnnotationsWithIndicesGroup } from '../../modules/renderer/RendererLeftPanel/Annotations/utils/resolveDropPosition';
import { CadLayer, CadObjectContext } from '../../contexts/CadLayersContext';
import { keyBy } from 'lodash/fp';

export enum RendererToolType {
  SELECT = 'SELECT',
  CLUSTER = 'CLUSTER',
  POINT = 'POINT',
  DISTANCE = 'DISTANCE',
  AREA = 'AREA',
  BOX = 'BOX',
  SURFACE = 'SURFACE',
  FLOORPLAN = 'FLOORPLAN',
  SCREENSHOT = 'SCREENSHOT',
}

export interface IClassification {
  identifier: string;
  name: string;
  code: number;
  color: [number, number, number, number];
  custom: boolean;
}

export const useClassifications = () => {
  const rendererContext = useContext(RendererContext);
  const viewer = rendererContext.viewer;
  const rerender = useRerender();

  useEffect(() => {
    if (!viewer) return;
    viewer.addEventListener('classifications_data_changed', rerender);
    return () => viewer.removeEventListener('classifications_data_changed', rerender);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!viewer]);

  return [
    {
      classifications: viewer?.classificationsInfo.classes || [],
      hiddenClassesByPointCloudId: viewer?.classificationsInfo.hiddenClassesByPointCloud || {},
    },
  ];
};

export const useSources = () => {
  const rendererContext = useContext(RendererContext);
  const viewer = rendererContext.viewer;

  return {
    sources: viewer?.sourcesInfo.sources || [],
  };
};

export const useAnnotations = () => {
  const sourceAnnotations = useAnnotationTree();

  // Build a stable index map instead of spreading new objects.
  // This preserves annotation object references so memo comparators work.
  // Depth-first pre-order over the tree, so nested groups (a floor's ROOM
  // folders) and their children get flat sequential indices too.
  const indexMap = useMemo(() => {
    const map = new Map<string, number>();
    let index = 0;
    const visit = (item: (typeof sourceAnnotations)[0]): void => {
      map.set(item.identifier, index);
      index += 1;
      if (isAnnotationContextGroup(item)) item.annotations.forEach(visit);
    };
    sourceAnnotations.forEach(visit);
    return map;
  }, [sourceAnnotations]);

  // Structural sharing: reuse previous objects when the source annotation
  // reference and computed index are unchanged. This keeps object references
  // stable so downstream React.memo comparators can short-circuit.
  //
  // Reading/writing refs inside useMemo is intentional here — we need synchronous
  // access to previous values for structural sharing (useEffect would cause a
  // render with stale data). The refs are only used for memoization, not rendering.
  const prevIndexedRef = useRef<AnnotationsWithIndices>([]);
  const prevSourceRef = useRef<typeof sourceAnnotations>([]);

  /* eslint-disable react-hooks/refs -- Reading/writing refs inside useMemo is intentional here:
     we need synchronous access to previous values for structural sharing. Using useEffect would
     cause a render with stale empty data. The refs are only used for memoization, not rendering. */
  const indexedAnnotations = useMemo(() => {
    const prev = prevIndexedRef.current;
    const prevSource = prevSourceRef.current;

    // Build lookup from previous results for O(1) access
    const prevByIdentifier = new Map<string, AnnotationsWithIndices[0]>();
    prev.forEach((a) => {
      prevByIdentifier.set(a.identifier, a);
    });
    const prevSourceByIdentifier = new Map<string, (typeof sourceAnnotations)[0]>();
    prevSource.forEach((a) => {
      prevSourceByIdentifier.set(a.identifier, a);
    });

    // Recursively index an item; groups walk their children (which can be
    // annotations OR nested groups — a floor's ROOM folders). Returns the
    // previous object when the source reference and every index are unchanged.
    const indexItem = (
      annotation: (typeof sourceAnnotations)[0],
      prevItem: AnnotationsWithIndices[0] | undefined,
      prevSrc: (typeof sourceAnnotations)[0] | undefined,
    ): AnnotationsWithIndices[0] => {
      const index = indexMap.get(annotation.identifier) ?? 0;
      // Source reference unchanged AND index unchanged → reuse previous object
      const sourceStable = prevSrc === annotation;

      if (isAnnotationContextGroup(annotation)) {
        const prevGroup = prevItem && isAnnotationContextGroup(prevItem) ? prevItem : undefined;
        const prevSrcGroup = prevSrc && isAnnotationContextGroup(prevSrc) ? prevSrc : undefined;
        const children = annotation.annotations.map((child) => {
          const prevChild = prevGroup?.annotations.find((c) => c.identifier === child.identifier);
          const prevSrcChild = prevSrcGroup?.annotations.find((c) => c.identifier === child.identifier);
          return indexItem(child, prevChild, prevSrcChild) as AnnotationsWithIndicesGroup['annotations'][0];
        });
        if (sourceStable && prevGroup && prevGroup.index === index && children.length === prevGroup.annotations.length && children.every((c, i) => c === prevGroup.annotations[i])) {
          return prevGroup;
        }
        return { ...annotation, index, annotations: children } as AnnotationsWithIndices[0];
      }

      if (sourceStable && prevItem && prevItem.index === index) return prevItem;
      return { ...annotation, index } as AnnotationsWithIndices[0];
    };

    const result = sourceAnnotations.map((annotation) =>
      indexItem(annotation, prevByIdentifier.get(annotation.identifier), prevSourceByIdentifier.get(annotation.identifier)),
    ) as AnnotationsWithIndices;

    prevIndexedRef.current = result;
    prevSourceRef.current = sourceAnnotations;
    return result;
  }, [sourceAnnotations, indexMap]);
  /* eslint-enable react-hooks/refs */

  const result = useMemo(() => ({ indexedAnnotations }), [indexedAnnotations]);
  return useMemo(() => [result] as const, [result]);
};

export const getCadObjectsByIdentifier = (cadLayers?: Array<Pick<CadLayer, 'identifier' | 'cadObjectGroups'>>) => {
  const cadObjectsByIdentifier = cadLayers?.flatMap((cadLayer) => cadLayer.cadObjectGroups?.flatMap((cadObjectGroup) => cadObjectGroup.cadObjects));
  return keyBy('identifier', cadObjectsByIdentifier);
};

export const useCADObjects = () => {
  const cadObjects = useContext(CadObjectContext);
  const cadObjectsByIdentifier = useMemo(() => {
    return getCadObjectsByIdentifier(cadObjects.cadLayers);
  }, [cadObjects.cadLayers]);
  return [{ ...cadObjects, cadObjectsByIdentifier }];
};

/** Map persona tool IDs to RendererToolType for shortcuts/video lookup */
const toolIdToRendererType: Record<string, RendererToolType> = {
  selection: RendererToolType.SELECT,
  point_measure: RendererToolType.POINT,
  distance_measure: RendererToolType.DISTANCE,
  area_measure: RendererToolType.AREA,
  box: RendererToolType.BOX,
  plane: RendererToolType.SURFACE,
  cluster: RendererToolType.CLUSTER,
  screenshot: RendererToolType.SCREENSHOT,
  floor_selector: RendererToolType.FLOORPLAN,
  auto_floor: RendererToolType.FLOORPLAN,
  manual_floor: RendererToolType.FLOORPLAN,
  line_detection: RendererToolType.FLOORPLAN,
  manual_room_draw: RendererToolType.FLOORPLAN,
  camera_orientation: RendererToolType.FLOORPLAN,
  surface_camera_orientation: RendererToolType.SURFACE,
  surface_line: RendererToolType.AREA,
  manual_surface_draw: RendererToolType.AREA,
};

export const useRendererTools = () => {
  const rendererContext = useContext(RendererContext);
  const viewer = rendererContext.viewer;
  const rerender = useRerender();

  const selectedTool = (() => {
    if (!viewer?.personaManager) return RendererToolType.SELECT;

    const activePersona = viewer.personaManager.getActivePersona();
    if (!activePersona) return RendererToolType.SELECT;

    const enabledTools = activePersona.getEnabledTools();
    // Find the first enabled exclusive tool (skip the default selection tool)
    for (const tool of enabledTools) {
      const mapped = toolIdToRendererType[tool.name];
      if (mapped) return mapped;
    }

    return RendererToolType.SELECT;
  })();

  // Listen to persona tool changes to re-render
  useEffect(() => {
    if (!viewer?.personaManager) return;

    const allPersonas = viewer.personaManager.getAllPersonas();
    const handlers: Array<{ persona: { removeEventListener(e: string, h: () => void): void }; handler: () => void }> = [];

    for (const persona of allPersonas) {
      persona.addEventListener('tool_enabled', rerender);
      persona.addEventListener('tool_disabled', rerender);
      handlers.push(
        { persona, handler: rerender },
      );
    }

    // Also listen for persona switches
    viewer.personaManager.addEventListener('persona_manager_stack_changed', rerender);

    return () => {
      for (const { persona } of handlers) {
        persona.removeEventListener('tool_enabled', rerender);
        persona.removeEventListener('tool_disabled', rerender);
      }
      viewer.personaManager.removeEventListener('persona_manager_stack_changed', rerender);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!viewer]);

  const destroyRenderer = useCallback(() => {
    if (!viewer) return;
    viewer?.toggleMapOverlay();
  }, [viewer]);

  const actions = useMemo(() => {
    return { destroyRenderer };
  }, [destroyRenderer]);

  const states = useMemo(() => {
    return { selectedTool };
  }, [selectedTool]);

  return [states, actions] as [typeof states, typeof actions];
};

export const useRenderer = () => {
  const [toolActions, toolState] = useRendererTools();

  const actions = useMemo(() => {
    return { ...toolActions };
  }, [toolActions]);

  const states = useMemo(() => {
    return { ...toolState };
  }, [toolState]);

  return [states, actions];
};
